import { useEffect, useRef, useState } from "react";
import { useDictationTranscriber } from "@/shared/lib/dictation";
import { t } from "@/shared/lib/translate";
import { IconButton } from "./IconButton";
import { AudioIcon, CloseIcon } from "./icons";

type DictationRun = { abort: AbortController; recorder?: MediaRecorder; stream?: MediaStream; timer?: ReturnType<typeof setTimeout> };

interface Props { target: React.RefObject<HTMLTextAreaElement | null>; disabled?: boolean }

/** Dictation inserts text only. It never submits a generation or replaces edits
 * the user made while their recording was being transcribed. */
export function DictationButton({ target, disabled }: Props) {
  const transcribeAudio = useDictationTranscriber();
  const [state, setState] = useState<"idle" | "starting" | "recording" | "transcribing">("idle");
  const [error,setError] = useState("");
  const run = useRef<DictationRun | null>(null);
  const current = useRef({ target, disabled }); current.current = { target, disabled };
  function hidden() { const node=current.current.target.current; return !node || current.current.disabled || Boolean(node.closest("[hidden], [inert]")); }
  function cancel() {
    const active=run.current; run.current=null;
    if (!active) return;
    active.abort.abort(); clearTimeout(active.timer);
    if (active.recorder?.state==="recording") active.recorder.stop();
    active.stream?.getTracks().forEach(track=>track.stop());
  }
  useEffect(()=>()=>cancel(),[]);
  useEffect(()=>{
    if (state==="idle") return;
    const check=()=>{if (document.hidden || hidden()) {cancel();setState("idle");}};
    const observer=new MutationObserver(check);
    observer.observe(document.body,{attributes:true,subtree:true,attributeFilter:["hidden","inert","disabled"]});
    document.addEventListener("visibilitychange",check); check();
    return()=>{observer.disconnect();document.removeEventListener("visibilitychange",check);};
  },[state,disabled]);

  async function start() {
    if (run.current || hidden() || !transcribeAudio) return;
    const node=target.current!;
    const original=node.value, start=node.selectionStart, end=node.selectionEnd;
    const active: DictationRun = {abort:new AbortController()};
    run.current=active;setState("starting");setError("");
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder==="undefined") throw new Error(t("audio.stt.microphoneUnavailable",{defaultValue:"Microphone recording is unavailable in this browser."}));
      const stream=await navigator.mediaDevices.getUserMedia({audio:true});
      if (active.abort.signal.aborted) {stream.getTracks().forEach(track=>track.stop());return;}
      active.stream=stream;
      const recorder=new MediaRecorder(stream);active.recorder=recorder;
      const chunks:Blob[]=[];
      recorder.ondataavailable=event=>{if(event.data.size)chunks.push(event.data);};
      recorder.onerror=()=>{cancel();setState("idle");setError(t("audio.stt.recordingFailed",{defaultValue:"Recording stopped unexpectedly. Please try again."}));};
      recorder.onstop=()=>{
        clearTimeout(active.timer);stream.getTracks().forEach(track=>track.stop());
        if(active.abort.signal.aborted || hidden())return;
        const mime=recorder.mimeType || "audio/webm";
        const file=new File(chunks,`dictation.${mime.includes("mp4")?"m4a":mime.includes("ogg")?"ogg":"webm"}`,{type:mime});
        setState("transcribing");
        void transcribeAudio(file,active.abort.signal).then(result=>{
          if(active.abort.signal.aborted || hidden() || !result.text)return;
          const currentNode=target.current!;
          const changed=currentNode.value!==original;
          const at=changed?currentNode.value.length:start, until=changed?at:end;
          const prefix=at>0 && !/\s$/.test(currentNode.value.slice(0,at))?" ":"";
          const suffix=until<currentNode.value.length && !/^\s/.test(currentNode.value.slice(until))?" ":"";
          const inserted=prefix+result.text+suffix;
          const value=currentNode.value.slice(0,at)+inserted+currentNode.value.slice(until);
          Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype,"value")!.set!.call(currentNode,value);
          currentNode.dispatchEvent(new Event("input",{bubbles:true}));
          currentNode.focus();currentNode.setSelectionRange(at+inserted.length,at+inserted.length);
        }).catch(reason=>{if(!active.abort.signal.aborted)setError(reason instanceof Error?reason.message:"Transcription failed.");})
          .finally(()=>{if(run.current===active){run.current=null;setState("idle");}});
      };
      recorder.start();setState("recording");
      active.timer=setTimeout(()=>{if(recorder.state==="recording")recorder.stop();},5*60*1000);
    } catch(reason) {
      if(!active.abort.signal.aborted) {cancel();setState("idle");setError(reason instanceof Error?reason.message:t("audio.stt.microphoneDenied",{defaultValue:"Allow microphone access to dictate."}));}
    }
  }
  if (!transcribeAudio) return null;
  const label=state==="recording"?t("audio.stt.stopDictation",{defaultValue:"Stop dictation"}):state==="transcribing"?t("audio.stt.cancelDictation",{defaultValue:"Cancel transcription"}):t("audio.stt.dictate",{defaultValue:"Dictate text"});
  return <div className="flex flex-wrap items-center justify-end gap-2 px-2 pb-1">
    {state!=="idle" && <span role="status" className="text-xs text-ink-muted">{state==="recording"?t("audio.stt.recording",{defaultValue:"Recording…"}):state==="transcribing"?t("audio.stt.transcribing",{defaultValue:"Transcribing…"}):t("audio.stt.starting",{defaultValue:"Opening microphone…"})}</span>}
    <IconButton size="sm" aria-label={label} tooltip={label} aria-pressed={state==="recording"} disabled={disabled || state==="starting"} onClick={()=>{
      if(state==="recording")run.current?.recorder?.stop();
      else if(state==="transcribing"){cancel();setState("idle");}
      else void start();
    }}>{state==="recording" || state==="transcribing"?<CloseIcon/>:<AudioIcon/>}</IconButton>
    {error && <p role="alert" className="w-full text-xs text-danger">{error}</p>}
  </div>;
}
